import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VisionMission = () => {
  const fadeInVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerChildren = {
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <section className="py-16 bg-background" id="vision-mission">
      <div className="container-executive">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerChildren}
          className="max-w-6xl mx-auto"
        >
          {/* Section Header */}
          <motion.div variants={fadeInVariants} className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
              <Icon name="Target" size={20} className="text-primary" />
              <span className="text-primary font-body-medium text-sm">Bootminds 2.0 Direction</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-heading font-heading-bold text-text-primary mb-6">
              Vision & Mission for the 2025 Relaunch
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Anchored in our 2017 roots, reimagined after the Intelliswift chapter, and engineered to help CXOs launch service-first GCC programs with absolute clarity.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Vision Section */}
            <motion.div variants={fadeInVariants} className="group">
              <div className="card-interactive rounded-2xl p-8 h-full bg-gradient-to-br from-primary/5 to-accent/5 hover:from-primary/10 hover:to-accent/10 transition-all duration-500">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center group-hover:bg-primary/30 transition-all duration-300">
                    <Icon name="Eye" size={32} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-heading font-heading-bold text-text-primary">Our Vision</h3>
                    <p className="text-text-secondary font-body-medium">Where We're Heading</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <p className="text-lg text-text-primary font-body leading-relaxed">
                    To be the most trusted partner for global enterprises re-establishing or expanding GCC operations in India with transparent economics and board-ready governance.
                  </p>
                  
                  <div className="space-y-4">
                    <h4 className="text-xl font-heading font-heading-bold text-text-primary">2025-26 Priorities</h4>
                    <ul className="space-y-3">
                      {[
                        "Launch five flagship GCC programs with transparent rate cards and zero-risk contracts",
                        "Build a 30-member CXO governance council sharing quarterly expansion playbooks",
                        "Publish an annual India GCC pricing and incentives benchmark",
                        "Co-create dual-city expansion frameworks with two Fortune 500 operators"
                      ].map((aspiration, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Icon name="Star" size={16} className="text-accent mt-1 flex-shrink-0" />
                          <span className="text-text-secondary">{aspiration}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-6 border-t border-border/50">
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={16} className="text-primary" />
                      <span className="text-sm text-text-secondary font-body-medium">Horizon: 2025-2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Mission Section */}
            <motion.div variants={fadeInVariants} className="group">
              <div className="card-interactive rounded-2xl p-8 h-full bg-gradient-to-br from-accent/5 to-primary/5 hover:from-accent/10 hover:to-primary/10 transition-all duration-500">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center group-hover:bg-accent/30 transition-all duration-300">
                    <Icon name="Compass" size={32} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-heading font-heading-bold text-accent">Our Mission</h3>
                    <p className="text-text-secondary font-body-medium">What We Do Today</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <p className="text-lg text-text-primary font-body leading-relaxed">
                    We design and operate service-first GCC programs that align four integrated pillars: Launch Blueprint, Expansion Playbooks, Executive Hiring, and SkillStack™ Staffing—delivering measurable milestones within transparent SLAs.
                  </p>
                  
                  <div className="space-y-4">
                    <h4 className="text-xl font-heading font-heading-bold text-accent">What We Promise Every Engagement</h4>
                    <ul className="space-y-3">
                      {[
                        "Co-create board-ready GCC plans with clear risk registers and governance cadences",
                        "Share complete pricing and incentive scenarios before contracts are signed",
                        "Stand up leadership and TA pods within the 25-day SkillStack™ talent promise",
                        "Embed walk-away options and transition playbooks to de-risk every decision"
                      ].map((commitment, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Icon name="CheckCircle" size={16} className="text-primary mt-1 flex-shrink-0" />
                          <span className="text-text-secondary">{commitment}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-6 border-t border-border/50">
                    <div className="flex items-center space-x-2">
                      <Icon name="Target" size={16} className="text-accent" />
                      <span className="text-sm text-text-secondary font-body-medium">Active Since: 2017 (Relaunched 2025)</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Core Values Section */}
          <motion.div variants={fadeInVariants} className="mt-16">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-heading font-heading-bold text-text-primary mb-4">
                Our Core Values
              </h3>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                The principles that guide every decision, interaction, and outcome we deliver.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: "Award",
                  title: "Excellence",
                  description: "Uncompromising commitment to delivering world-class results"
                },
                {
                  icon: "Handshake",
                  title: "Integrity",
                  description: "Transparent, honest, and ethical in all our partnerships"
                },
                {
                  icon: "Zap",
                  title: "Innovation",
                  description: "Pioneering solutions that transform global operations"
                },
                {
                  icon: "Users",
                  title: "Partnership",
                  description: "Building lasting relationships through collaborative success"
                }
              ].map((value, index) => (
                <div key={index} className="group">
                  <div className="card-interactive rounded-xl p-6 text-center h-full hover:shadow-xl transition-all duration-300">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-all duration-300">
                      <Icon name={value.icon} size={24} className="text-primary" />
                    </div>
                    <h4 className="text-xl font-heading font-heading-bold text-text-primary mb-3">
                      {value.title}
                    </h4>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={fadeInVariants} className="mt-16 text-center">
            <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8">
              <h3 className="text-2xl font-heading font-heading-bold text-text-primary mb-4">
                Ready to Explore Our Launch Blueprint?
              </h3>
              <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
                Connect with the team behind Bootminds’ 2025 relaunch to align transparent economics, risk guardrails, and SkillStack™ talent models for your GCC ambition.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  variant="default"
                  size="lg"
                  className="bg-primary hover:bg-primary/90"
                  iconName="Calendar"
                  iconPosition="left"
                  asChild
                >
                  <Link to="/executive-contact-strategic-engagement">
                    Schedule Origin Briefing
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Flag"
                  iconPosition="left"
                  asChild
                >
                  <Link to="/services#launch-blueprint">
                    View Launch Blueprint
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default VisionMission;